import api, { uploadApi } from './axios';

/**
 * Create a comment on a post or reply to another comment
 * @param {Object} commentData - Comment data
 * @param {string} commentData.content - Comment content
 * @param {string} commentData.postId - Post ID
 * @param {string} commentData.parentId - Parent comment ID (optional for replies)
 * @param {File} commentData.media - Media file (optional)
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const createComment = (commentData, onUploadProgress) => {
  const { content, postId, parentId, media } = commentData;
  
  const formData = new FormData();
  formData.append('content', content);
  formData.append('postId', postId);
  
  if (parentId) {
    formData.append('parentId', parentId);
  }
  
  if (media) {
    formData.append('media', media);
  }
  
  return uploadApi('/comments', formData, onUploadProgress);
};

/**
 * Get comments for a post
 * @param {string} postId - Post ID
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {boolean} options.parentOnly - Only return top-level comments
 * @returns {Promise} Promise object representing the API response
 */
export const getPostComments = (postId, options = {}) => {
  const { page = 1, limit = 10, parentOnly = true } = options;
  
  return api.get(`/comments/post/${postId}`, {
    params: {
      page,
      limit,
      parentOnly,
    },
  });
};

/**
 * Get a comment by ID
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const getCommentById = (commentId) => {
  return api.get(`/comments/${commentId}`);
};

/**
 * Update a comment
 * @param {string} commentId - Comment ID
 * @param {Object} commentData - Updated comment data
 * @returns {Promise} Promise object representing the API response
 */
export const updateComment = (commentId, commentData) => {
  return api.put(`/comments/${commentId}`, commentData);
};

/**
 * Delete a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const deleteComment = (commentId) => {
  return api.delete(`/comments/${commentId}`);
};

/**
 * Like a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const likeComment = (commentId) => {
  return api.put(`/comments/${commentId}/like`);
};

/**
 * Unlike a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const unlikeComment = (commentId) => {
  return api.delete(`/comments/${commentId}/like`);
};

/**
 * Get replies to a comment
 * @param {string} commentId - Comment ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getCommentReplies = (commentId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/comments/${commentId}/replies`, {
    params: {
      page,
      limit,
    },
  });
};